<template>
  <transition name="fade" mode="out-in">
    <div id="associate" key="associate" class="application-section">
      <section id="registration">
        <div class="container is-fluid registered-text">
          <ChooseQuestion ref="registration" :options="previouslyRegistered" question="I" :tab="sections.registration.tab"
                          closingSentence="been on the register of pharmacists in Great Britain."
                          @question-answered="registration" :class="{error: $v.understandingYou.previouslyRegistered.$error}" :isMandatory="true"
                          :existingValue="understandingYou.previouslyRegistered"  />
          <p v-if="displayNote && !isMobile" class="registered">
            If you've previously been on the register of pharmacists of Great Britain then you can't join the RPS as an Associate.
            <a href="#" @click.prevent="afterEnter">Have you considered joining as a Member?</a>
          </p>
        </div>
      </section>
      <section id="degreeType" v-bind:class="sections.degreeType">
        <div class="container is-fluid">
          <div :class="{'inline-question':!isMobile}">
            <ChooseQuestion question="I have an" :options="degreeTypes" :tab="sections.degreeType.tab"
                            @question-answered="degreeOptionAnswered" closingSentence="degree,"
                            :class="{error: $v.understandingYou.degreeType.$error}" :isDisabled="sections.degreeType.ghost"
                            :isMandatory="true" :existingValue="understandingYou.degreeType" />&nbsp;
            <DateQuestion question="obtained on" @question-answered="degreeAwardedEntered" :tab="sections.degreeType.tab"
                          :class="{error: $v.understandingYou.degreeAwarded.$error,}"
                          :isDisabled="sections.degreeType.ghost" :isMandatory="true" :existingValue="understandingYou.degreeAwarded" />
          </div>
          <div>
            <ChooseQuestion question="from" :options="universities" :tab="sections.degreeType.tab"
                            :class="{error: $v.understandingYou.university.$error}"
                            @question-answered="universitiesAnswered" closingSentence="." :isDisabled="sections.degreeType.ghost" :isMandatory="true"
                            :isDropdownControlled="true" :existingValue="understandingYou.university" />
            <FreetextQuestion question="Please provide the full title of your qualification:"
                              :class="{hidden: !showFullQualification, error: $v.understandingYou.otherQualification.$error}"
                              v-bind:isDisabled="!showFullQualification" closingTag="." :tab="sections.degreeType.tab"
                              @question-answered="understandingYou.otherQualification = $event;"
                              :isMandatory="showFullQualification" :isDropdownControlled="showFullQualification" :isExtraQuestion="true"
                              :isNotDisplayed="true" :existingValue="understandingYou.otherQualification" maxlength="40" />
            <div v-if="showOverseasInformation">
              <FreetextQuestion question="Please state the name of the institution where you studied:"
                                :class="{error: $v.understandingYou.alternativeUniversity.$error}" :tab="sections.degreeType.tab"
                                closingTag="." @question-answered=" understandingYou.alternativeUniversity = $event;" :isMandatory="true"
                                :isDropdownControlled="true" :isExtraQuestion="true" :isNotDisplayed="true"
                                :existingValue="understandingYou.alternativeUniversity" maxlength="255"/>
              <AutocompleteQuestion question="Please state which country you studied in:" :tab="sections.degreeType.tab"
                                    :class="{error: $v.understandingYou.alternativeCountry.$error}"
                                    @question-answered="understandingYou.alternativeCountry = $event;" closingTag="." :placeholder="'Country'"
                                    :isMandatory="true" :isExtraQuestion="true" :isNotDisplayed="true"
                                    :existingValue="understandingYou.alternativeCountry" :dataSource="countries" />
            </div>
          </div>
        </div>
      </section>
      <section id="sector" :class="sections.sector">
        <div class="container is-fluid">
          <ChooseQuestion question="My primary sector of pharmacy is" :options="sectors" :tab="sections.sector.tab"
                          @question-answered="understandingYou.primarySector = $event.text" closingSentence="."
                          :isDisabled="sections.sector.ghost" :isDropdownControlled="true" :existingValue="understandingYou.primarySector" />
          <ChooseQuestion question="My primary occupation in pharmacy is best described as" :options="associateOccupations"
                          @question-answered="understandingYou.primaryOccupation = $event.text" closingSentence="."  :tab="sections.sector.tab"
                          :isDisabled="sections.sector.ghost" :isDropdownControlled="true" :existingValue="understandingYou.primaryOccupation" />
        </div>
      </section>
      <section id="employment" :class="sections.employment">
        <div class="container is-fluid">
          <ChooseQuestion question="I am currently" :options="employmentStatuses" :tab="sections.employment.tab"
                          :class="[{ mandatory : true, error: $v.understandingYou.employmentStatus.$error}, 'employment-size']"
                          @question-answered="employment" closingSentence="." :isDropdownControlled="true"
                          :existingValue="understandingYou.employmentStatus" />
          <FreetextQuestion :question="workStatement"  :tab="sections.employment.tab"
                            @question-answered="understandingYou.job = $event" :existingValue="understandingYou.job" closingTag="." />
          <FreetextQuestion question="at" @question-answered="understandingYou.organization = $event" closingSentence="." :isNotDisplayed="true"
                          :existingValue="understandingYou.organisation" :tab="sections.employment.tab" maxlength="100" />
        </div>
      </section>
      <section id="prescriber" :class="sections.prescriber">
        <div class="container is-fluid">
          <p>I am a:</p>
          <CheckboxQuestion question="Independent prescriber" @question-answered="understandingYou.isIndependentPrescriber = $event"
                            :isDisabled="sections.prescriber.ghost" :existingValue="understandingYou.isIndependentPrescriber"
                            :tab="sections.prescriber.tab" />
          <CheckboxQuestion question="Supplementary prescriber" @question-answered="understandingYou.isSupplementaryPrescriber = $event"
                            :isDisabled="sections.prescriber.ghost" :existingValue="understandingYou.isSupplementaryPrescriber"
                            :tab="sections.prescriber.tab"/>
          <CheckboxQuestion question="Designated Supervisor" @question-answered="understandingYou.isPreRegTutor = $event"
                            :isDisabled="sections.prescriber.ghost" :existingValue="understandingYou.isPreRegTutor"
                            :tab="sections.prescriber.tab"/>
          <div v-if="!this.$store.state.isMobile" id="confirm-wrap">
            <button type="button" class="btn-confirm" @click.stop.prevent="$emit('confirmClicked')">Next</button>
          </div>
        </div>
      </section>
    </div>
  </transition>
</template>
<script>
import { mapGetters } from 'vuex';
import '@/styles/application.scss';
import '@/styles/buefy.scss';
import ChooseQuestion from '@/components/ChooseQuestion.vue';
import FreetextQuestion from '@/components/FreetextQuestion.vue';
import DateQuestion from '@/components/DateQuestion.vue';
import CheckboxQuestion from '@/components/CheckboxQuestion.vue';
import AutocompleteQuestion from '@/components/AutocompleteQuestion.vue';
import { required, requiredIf, minLength } from 'vuelidate/lib/validators';
import ghost from '@/understandingyou/scripts/ghost';
import Manager from '@/applicationManager';

export default {
  name: 'Associate',
  components: {
    ChooseQuestion,
    FreetextQuestion,
    DateQuestion,
    CheckboxQuestion,
    AutocompleteQuestion,
  },
  props: {
    dataGetter: Function,
  },
  async mounted() {
    this.countries = await Manager.getCountryList();
  },
  updated() {
    this.$emit('onFinalSection', this.isFinalSectionVisible);
  },
  computed: {
    ...mapGetters(['universities', 'sectors', 'employmentStatuses', 'associateOccupations']),
    isFinalSectionVisible() {
      return this.sections.prescriber.hidden === false;
    },
    isMobile() {
      return this.$store.state.isMobile;
    },
    workStatement() {
      return ghost.workStatement(this.understandingYou.employmentStatus);
    },
    showFullQualification() {
      return this.understandingYou.degreeType === 'Other';
    },
    showOverseasInformation() {
      return this.understandingYou.university === 'Other';
    },
  },
  data() {
    return {
      previouslyRegistered: [{ text: 'have never', value: '1' }, { text: 'have', value: '2' }],
      degreeTypes: [{ value: 1, text: 'MPharm' }, { value: 2, text: 'Other' }],
      displayNote: false,
      sections: {
        registration: { hidden: false, ghost: false, tab: 0 },
        degreeType: { hidden: false, ghost: true, tab: this.$store.state.isMobile ? -1 : 0 },
        sector: { hidden: true, ghost: false, tab: this.$store.state.isMobile ? -1 : 0 },
        employment: { hidden: true, ghost: false, tab: this.$store.state.isMobile ? -1 : 0 },
        prescriber: { hidden: true, ghost: false, tab: this.$store.state.isMobile ? -1 : 0 },
      },
      understandingYou: this.dataGetter(),
      countries: [],
    };
  },
  methods: {
    registration(previously) {
      this.displayNote = previously.text === 'have';
      this.understandingYou.previouslyRegistered = previously.text;
      if (this.displayNote) {
        this.sections.employment.hidden = true;
        this.sections.prescriber.hidden = true;
        if (this.isMobile) {
          this.displayDialog();
          this.$refs.registration.resetSelected();
          this.understandingYou.previouslyRegistered = null;
        } else {
          this.sections.degreeType.hidden = true;
          this.sections.sector.hidden = true;
        }
        // if we want to make sure when data is there all fields are showing.
      } else if (this.understandingYou.university !== ''
        || this.understandingYou.employmentStatus !== '') {
        ghost.showSection(this.sections.degreeType);
        ghost.showSection(this.sections.sector);
        ghost.showSection(this.sections.employment);
        ghost.showSection(this.sections.prescriber);
      } else {
        ghost.showSection(this.sections.degreeType);
        ghost.ghostSection(this.sections.sector);
      }
    },
    degreeAwardedEntered(d) {
      this.$v.understandingYou.degreeAwarded.$touch();
      this.understandingYou.degreeAwarded = d;
    },
    degreeOptionAnswered(qualificationType) {
      this.understandingYou.degreeType = qualificationType.text;
    },
    universitiesAnswered(university) {
      this.understandingYou.university = university.text;
      ghost.showSection(this.sections.sector);
      ghost.showSection(this.sections.employment);
      ghost.ghostSection(this.sections.prescriber);
    },
    employment(status) {
      this.understandingYou.employmentStatus = status.text;
      ghost.showSection(this.sections.prescriber);
    },
    afterEnter() {
      this.$emit('kill-me', this);
    },
    displayDialog() {
      this.$buefy.dialog.confirm({
        title: "There's a problem",
        message: "If you've previously been on the GB register of pharmacists"
         + ' then you can\'t join the RPS as an Associate. Have you considered joining as a Member?',
        cancelText: 'Go back to Associate',
        confirmText: 'Join as Member',
        onConfirm: () => this.afterEnter(),
      });
    },
    HasErrorInSection(current) {
      const { understandingYou } = this.$v;
      const sectionValidations = {
        registration() {
          understandingYou.previouslyRegistered.$touch();
          return understandingYou.previouslyRegistered.$error;
        },
        degreeType() {
          understandingYou.degreeType.$touch();
          understandingYou.otherQualification.$touch();
          understandingYou.degreeAwarded.$touch();
          understandingYou.university.$touch();
          understandingYou.alternativeUniversity.$touch();
          understandingYou.alternativeCountry.$touch();
          return understandingYou.degreeAwarded.$error || understandingYou.university.$error
            || understandingYou.degreeType.$error || understandingYou.otherQualification.$error
            || understandingYou.alternativeUniversity.$error || understandingYou.alternativeCountry.$error;
        },
        employment() {
          understandingYou.employmentStatus.$touch();
          return understandingYou.employmentStatus.$error;
        },
      };

      const validation = sectionValidations[current];

      if (validation) {
        return validation();
      }
      return false;
    },
    SetIndexes(current, next) {
      this.sections[current].tab = -1;
      this.sections[next].tab = 0;
    },
  },
  validations() {
    return {
      understandingYou: {
        previouslyRegistered: {
          required,
        },
        degreeType: {
          required,
        },
        otherQualification: {
          required: requiredIf(() => this.showFullQualification),
        },
        degreeAwarded: {
          required,
          minLength: minLength(10),
        },
        university: {
          required,
        },
        alternativeUniversity: {
          required: requiredIf(() => this.showOverseasInformation),
        },
        alternativeCountry: {
          required: requiredIf(() => this.showOverseasInformation),
        },
        employmentStatus: {
          required,
        },
      },
    };
  },
};
</script>
<style lang="scss">
  @import '../../styles/mixins';

  #associate #degreeType .inline-question {
    div.date-control input.question-control
  {
    @include browser(Trident/7.0) {
      display: inline-block;
    }
  }

  div.scrollable, div.date-control {
    @include browser(Trident/7.0) {
      width: 100%;
    }
  }
  }
  /*call modal builtin classes stop increase font-face size*/
  .modal-card .modal-card-foot .button {
    font-size: 13.5px !important;
  }
  .modal-card-title {
    font-size: 18px !important;
  }
  /*use poppin for member registered text*/
  #registration div.registered-text p.registered {
    @include DefaultPoppinFont();
  }
</style>
